import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { useTableArtifacts } from './useTableArtifacts';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { DataTableVariant } from '../table/TableTypes';
import { telemetry } from '../../../telemetry';
import { useArtifactPanelWidth } from '../../../hooks/useArtifactPanelWidth';
import { parseMarkdownTable } from './parseMarkdownTable';
import { VirtualizedMarkdownTable } from './VirtualizedMarkdownTable';
import { ResizableInlinePanel } from '../panel/ResizableInlinePanel';
import { downloadCsv } from '../../../lib/downloadCsv';

/**
 * Parse a markdown table and convert it to CSV format
 */
function markdownTableToCsv(markdown: string): string {
  const lines = markdown.trim().split('\n');
  const csvRows: string[] = [];

  for (const line of lines) {
    // Skip separator lines (containing only |, -, and spaces)
    if (/^\|[\s\-:|]+\|$/.test(line.trim())) {
      continue;
    }

    // Parse table row
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const cells = line
        .trim()
        .slice(1, -1) // Remove leading and trailing |
        .split('|')
        .map((cell) => {
          const trimmed = cell.trim();
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (trimmed.includes(',') || trimmed.includes('"') || trimmed.includes('\n')) {
            return `"${trimmed.replace(/"/g, '""')}"`;
          }
          return trimmed;
        });

      csvRows.push(cells.join(','));
    }
  }

  return csvRows.join('\n');
}

const TableSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
    <Skeleton variant="rectangular" height={40} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
  </Box>
);

const TableErrorState = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      height: '100%',
    }}
  >
    <Typography variant="body" color={colors.blush[600]}>
      Failed to load table data. Please try again.
    </Typography>
  </Box>
);

export const TableArtifactPanel: React.FC = () => {
  const {
    isTablePanelOpen,
    currentTableId,
    closeTablePanel,
    tableArtifacts,
    isLoading,
    error,
    setTableData,
    setLoading,
    setError,
  } = useTableArtifacts();

  const { get_table_artifact } = useAiAgentApiFacade();
  const fetchingRef = useRef<string | null>(null);
  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useArtifactPanelWidth();

  // Fetch table data when panel opens
  useEffect(() => {
    if (!isTablePanelOpen || !currentTableId) {
      return;
    }

    // Already have the data cached
    if (tableArtifacts[currentTableId]) {
      return;
    }

    // Already fetching this table
    if (fetchingRef.current === currentTableId) {
      return;
    }

    const fetchTableData = async () => {
      fetchingRef.current = currentTableId;
      setLoading(true);
      setError(null);

      try {
        const data = await get_table_artifact(currentTableId);
        setTableData(currentTableId, data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch table'));
        telemetry.captureError(err);
      } finally {
        setLoading(false);
        fetchingRef.current = null;
      }
    };

    fetchTableData();
  }, [
    isTablePanelOpen,
    currentTableId,
    tableArtifacts,
    get_table_artifact,
    setTableData,
    setLoading,
    setError,
  ]);

  const tableData = currentTableId ? tableArtifacts[currentTableId] : null;

  // Parse markdown table into structured data for virtualized rendering
  const parsedTable = useMemo(() => {
    if (!tableData?.markdown) return null;
    return parseMarkdownTable(tableData.markdown);
  }, [tableData?.markdown]);

  const handleDownloadCsv = useCallback(() => {
    if (!tableData?.markdown) return;

    try {
      const csvContent = markdownTableToCsv(tableData.markdown);
      const filename = `table-${currentTableId || 'data'}.csv`;
      downloadCsv(csvContent, filename);
    } catch (err) {
      telemetry.captureError(err);
    }
  }, [tableData, currentTableId]);

  if (!isTablePanelOpen) {
    return null;
  }

  const renderContent = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (error || !tableData || !parsedTable) {
      return <TableErrorState />;
    }

    return (
      <Box
        sx={{
          p: 2,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <VirtualizedMarkdownTable
          parsedTable={parsedTable}
          variant={DataTableVariant.medium}
          originalMarkdown={tableData.markdown}
        />
      </Box>
    );
  };

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0 8px 16px',
      }}
    >
      <Typography variant="h5">Table Data</Typography>
      {tableData && (
        <Button startIconName="Download" size="small" variant="system" onClick={handleDownloadCsv}>
          Download CSV
        </Button>
      )}
    </Box>
  );

  return (
    <ResizableInlinePanel
      panelWidth={panelWidth}
      onResize={updatePanelWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onClose={closeTablePanel}
      header={header}
    >
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{renderContent()}</Box>
    </ResizableInlinePanel>
  );
};
